import React, { useRef, useMemo } from 'react';

import { CustomerActionsPopover } from './CustomerActionsPopover';
import { DataTable, Column } from '../DataTable';
import { PaginatedResponse, PaylinkCustomerInfoResponse } from '../../store/modules/paylink/types';
import {
  SearchPaylinkCustomersQuery,
  SearchPaylinkCustomersSortQuery,
} from '../../store/modules/merchant/types';
import { Pagination } from '../DataTable/Pagination';
import { CardOnFileCell } from './CardOnFileCell';
import { useMerchant } from '../../contexts/MerchantContext';

const allColumns: Column<PaylinkCustomerInfoResponse>[] = [
  {
    headerName: 'Customer Name',
    valueGetter: (row) => `${row.firstName} ${row.lastName}`,
    width: 168,
    minWidth: 168,
    sortable: true,
    sortName: 'firstName',
  },
  {
    headerName: 'Email',
    field: 'email',
    width: 188,
    sortable: true,
  },
  {
    headerName: 'Phone number',
    field: 'phone',
    width: 150,
    valueGetter: (row) =>
      row.phone
        ? `${row.phone.substring(0, 3)}-${row.phone.substring(3, 6)}-${row.phone.substring(6)}`
        : '',
  },
  {
    headerName: 'Card on file',
    id: 'cardOnFile',
    renderCell: (row) => (
      <CardOnFileCell
        networkType={row.networkType}
        lastFour={row.lastFour}
        cardArtUrl={row.cardArtUrl}
      />
    ),
  },
  {
    id: 'details',
    headerName: '',
    width: 28,
    paddingRight: 33,
    renderCell: (row) => <CustomerActionsPopover customer={row} />,
  },
];

export type TableParams = Required<Pick<SearchPaylinkCustomersQuery, 'pageNumber' | 'pageSize'>> &
  Partial<Pick<SearchPaylinkCustomersQuery, 'sortBy' | 'orderBy'>>;

interface Props {
  customerData?: PaginatedResponse<PaylinkCustomerInfoResponse>;
  isLoading: boolean;
  tableParams: TableParams;
  setTableParams: (tableParams: TableParams) => void;
}

export const CustomersTable = ({ customerData, isLoading, tableParams, setTableParams }: Props) => {
  const { pageNumber, pageSize, sortBy, orderBy } = tableParams;
  const tableRef = useRef<HTMLDivElement>(null);
  const { hasCardOnFileAccess } = useMerchant();

  // Use hook to check card on file access and filter columns accordingly
  const hasCardAccess = hasCardOnFileAccess();

  const columns = useMemo(() => {
    return allColumns.filter((column) => {
      // Filter out card on file column if user doesn't have access
      if (column.id === 'cardOnFile') {
        return hasCardAccess;
      }
      return true;
    });
  }, [hasCardAccess]);

  return (
    <DataTable
      rows={customerData?.data ?? []}
      columns={columns}
      getRowId={(row) => row.payLinkCustomerId}
      isLoading={isLoading}
      pageSize={pageSize}
      sort={{ sortBy, orderBy }}
      setSort={(query) =>
        setTableParams({ ...tableParams, ...(query as SearchPaylinkCustomersSortQuery) })
      }
      tableHeight="calc(100vh - 315px)"
      noRowsMessage="No Customers found. Try changing the filters or search term."
      tableRef={tableRef}
      renderPagination={() => (
        <Pagination
          itemCount={customerData?.data.length ?? 0}
          itemTotal={customerData?.metadata.totalRecords ?? 0}
          pageCount={customerData?.metadata.totalPages ?? 0}
          onPageChange={(_, p) => {
            setTableParams({ ...tableParams, pageNumber: p });
            tableRef.current?.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }}
          page={pageNumber}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => {
            setTableParams({ ...tableParams, pageNumber: 1, pageSize: newPageSize });
          }}
          disabled={isLoading}
        />
      )}
    />
  );
};
